import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useResizeObserver } from './hooks/useResizeObserver';
import './App.css';
import Layout from './components/layout/Layout';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { getHeaderContent as getPortfolioHeaderContent } from './components/layout/PortfolioManagement';
import { getHeaderContent as getTechnicalAnalysisHeaderContent } from './components/layout/TechnicalAnalysis';
import { getHeaderContent as getProjectTrackerHeaderContent } from './components/layout/ProjectTracker';
import Loading from './components/common/Loading';

// Define the possible progress steps
type ProgressStep = 'Evaluation' | 'Analysis' | 'Action';

// Define the interface for the PortfolioManagement component handle without importing it directly
// This matches the actual PortfolioManagementHandle interface from PortfolioManagement.tsx
interface PortfolioManagementHandleType {
  getFormData: () => PortfolioFormData | null;
  generateTemplate: () => string | null;
}

// Define the interface for the TechnicalAnalysis component handle
interface TechnicalAnalysisHandleType {
  getFormData: () => TechnicalAnalysisFormData | null;
  generateTemplate: () => string | null;
}

// Define the interface for the ProjectTracker component handle
interface ProjectTrackerHandleType {
  getFormData: () => ProjectTrackerFormData | null;
  generateTemplate: () => string | null;
}

// Define the PortfolioFormData interface to match the one in Body.tsx
interface PortfolioFormData {
  investmentAmount: number;
  riskLevel: string;
  expectedReturn: number;
  maxDrawdown: number;
  investmentPeriod: number;
  inclusions: string[];
  exclusions: string[];
}

// Define the TechnicalAnalysisFormData interface
interface TechnicalAnalysisFormData {
  cryptocurrency: string;
  timeFrame: 'Short-term' | 'Medium-term' | 'Long-term';
}

// Define the ProjectTrackerFormData interface
interface ProjectTrackerFormData {
  project: string;
}

// Define lazy-loaded components at the module level
const PortfolioManagementComponent = React.lazy(() => import('./components/layout/PortfolioManagement'));
const TechnicalAnalysisComponent = React.lazy(() => import('./components/layout/TechnicalAnalysis'));
const ProjectTrackerComponent = React.lazy(() => import('./components/layout/ProjectTracker'));

// Component to hold the main application logic and routing, allowing use of useLocation
function AppContent() {
  const portfolioManagementRef = useRef<PortfolioManagementHandleType>(null);
  const technicalAnalysisRef = useRef<TechnicalAnalysisHandleType>(null);
  const projectTrackerRef = useRef<ProjectTrackerHandleType>(null);
  const [portfolioFormValid, setPortfolioFormValid] = useState(false);
  const [technicalAnalysisFormValid, setTechnicalAnalysisFormValid] = useState(false);
  const [projectTrackerFormValid, setProjectTrackerFormValid] = useState(false);
  const [activeProgressStep, setActiveProgressStep] = useState<ProgressStep>('Evaluation');
  const location = useLocation();

  const handleTriggerBodySubmit = () => {
    const currentPath = location.pathname;
    let template = null;
    
    if (currentPath === '/portfolio-management' && portfolioManagementRef.current) {
      template = portfolioManagementRef.current.generateTemplate();
    } else if (currentPath === '/technical-analysis' && technicalAnalysisRef.current) {
      template = technicalAnalysisRef.current.generateTemplate();
    } else if (currentPath === '/project-tracker' && projectTrackerRef.current) {
      template = projectTrackerRef.current.generateTemplate();
    }
    
    if (template) {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ type: 'INPUT_TO_AGENT_PROMPT', text: template }, '*');
      }
      
      if (activeProgressStep === 'Evaluation') {
        setActiveProgressStep('Analysis');
      }
    }
  };

  const handlePortfolioFormValidityChange = (isValid: boolean) => {
    setPortfolioFormValid(isValid);
  };

  const handleTechnicalAnalysisFormValidityChange = (isValid: boolean) => {
    setTechnicalAnalysisFormValid(isValid);
  };

  const handleProjectTrackerFormValidityChange = (isValid: boolean) => {
    setProjectTrackerFormValid(isValid);
  };

  const renderPortfolioManagement = () => {
    const headerContent = getPortfolioHeaderContent();
    return (
      <React.Suspense fallback={<Loading />}>
        <Layout 
          onStartAnalysis={handleTriggerBodySubmit} 
          canStartAnalysis={portfolioFormValid}
          currentStep={activeProgressStep}
          headerContent={headerContent}
        >
          <PortfolioManagementComponent ref={portfolioManagementRef} onValidityChange={handlePortfolioFormValidityChange} />
        </Layout>
      </React.Suspense>
    );
  };

  const renderTechnicalAnalysis = () => {
    const headerContent = getTechnicalAnalysisHeaderContent();
    return (
      <React.Suspense fallback={<Loading />}>
        <Layout 
          onStartAnalysis={handleTriggerBodySubmit} 
          canStartAnalysis={technicalAnalysisFormValid}
          currentStep={activeProgressStep}
          headerContent={headerContent}
        >
          <TechnicalAnalysisComponent ref={technicalAnalysisRef} onValidityChange={handleTechnicalAnalysisFormValidityChange} />
        </Layout>
      </React.Suspense>
    );
  };

  const renderProjectTracker = () => {
    const headerContent = getProjectTrackerHeaderContent();
    return (
      <React.Suspense fallback={<Loading />}>
        <Layout 
          onStartAnalysis={handleTriggerBodySubmit} 
          canStartAnalysis={projectTrackerFormValid}
          currentStep={activeProgressStep}
          headerContent={headerContent}
        >
          <ProjectTrackerComponent ref={projectTrackerRef} onValidityChange={handleProjectTrackerFormValidityChange} />
        </Layout>
      </React.Suspense>
    );
  };

  useEffect(() => {
    setActiveProgressStep('Evaluation');
  }, [location.pathname]);
  
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/portfolio-management" replace />} />
      <Route path="/portfolio-management" element={renderPortfolioManagement()} />
      <Route path="/technical-analysis" element={renderTechnicalAnalysis()} />
      <Route path="/project-tracker" element={renderProjectTracker()} />
    </Routes>
  );
}

function App() {
  const sendResizeMessage = useCallback((height: number) => {
    const isInIframe = window.parent && window.parent !== window;
    if (isInIframe) {
      window.parent.postMessage({ 
        type: 'RESIZE_IFRAME',
        height: height
      }, '*');
    }
  }, []);

  const appWrapperRef = useResizeObserver(sendResizeMessage);

  // Function to dynamically load a script
  const loadScript = useCallback((scriptUrl: string) => {
    const script = document.createElement('script');
    script.src = scriptUrl;
    script.async = true;
    script.onerror = () => {
      console.error(`Failed to load script: ${scriptUrl}`);
    };
    document.body.appendChild(script);
  }, []);

  // Listen for messages from parent window
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      console.log(event.data)
      // Check if the message is of type LOAD_API_CLIENT_SCRIPT
      if (event.data && event.data.type === 'LOAD_API_CLIENT_SCRIPT' && event.data.scriptUrl) {
        console.log(`Loading API client script from: ${event.data.scriptUrl}`);
        loadScript(event.data.scriptUrl);
      }
    };

    // Add event listener for messages
    window.addEventListener('message', handleMessage);

    // Clean up the event listener when component unmounts
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [loadScript]);

  return (
    <div ref={appWrapperRef} className="app-root-wrapper"> 
      <Router>
        <AppContent />
      </Router>
    </div>
  );
}

export default App;
